"use client";

import React from "react";
import { Phone, Clock, Mail, CircleCheck, Loader, Leaf, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { useFormContext } from "react-hook-form";
export default function SupportUs() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  return <section data-section-id="1794" id="consultation-portal" ref={sectionRef} className="bg-background py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className={cn("flex flex-col lg:flex-row items-center lg:items-stretch transition-all duration-1000 ease-out", {
        isSectionVisible
      } ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          
          <div className="w-full lg:w-7/12 bg-gradient-to-br from-[#095fa3] to-[#074b82] text-white rounded-xl p-8 sm:p-12 lg:p-16 lg:pr-32 flex flex-col justify-center relative z-0 shadow-lg">
            <div className="space-y-8">
              <div>
                <p className="text-sm font-bold uppercase tracking-widest text-[#fb8c26] mb-4">
                  CONTACT US
                </p>
                <h2 className="text-4xl sm:text-5xl font-bold font-sans mb-6 leading-tight tracking-tight">
                  Modernize Your Agency Today
                </h2>
                <p className="text-white/90 text-xl font-sans leading-relaxed">Partner with us to transform your governmental operations with our industry-leading ERP solutions. <br></br>Schedule a free technical consultation.</p>
                <br></br>
                <br></br>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-white/10 p-3 rounded-md shrink-0">
                    <Phone className="size-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-white/70 font-medium uppercase tracking-wider">
                      Direct Support Line
                    </p>
                    <Link to="tel:18004683771" className="text-2xl font-bold hover:text-[#fb8c26] transition-colors block mt-1">+1 720-772-6420</Link>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-white/10 p-3 rounded-md shrink-0">
                    <Clock className="size-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-white/70 font-medium uppercase tracking-wider">
                      Operational Hours
                    </p>
                    <p className="text-lg font-medium mt-1">Mon-Fri: 9:00 AM - 5:00 PM MST</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-white/10 p-3 rounded-md shrink-0">
                    <Mail className="size-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-white/70 font-medium uppercase tracking-wider">
                      Procurement Email
                    </p>
                    <p className="text-lg font-medium mt-1">info@kanha.biz</p>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-white/20">
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
                  <p className="flex items-center gap-2 text-sm text-white/90">
                    <CircleCheck className="size-4 text-[#fb8c26]" />
                    <span>White-Glove Onboarding</span>
                  </p>
                  <p className="flex items-center gap-2 text-sm text-white/90">
                    <CircleCheck className="size-4 text-[#fb8c26]" />
                    <span>FedRAMP Compliance Ready</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          
          <div ref={formRef} className="w-full lg:w-5/12 mt-8 lg:mt-0 lg:-ml-20 relative z-10 flex items-center">
            <div className="bg-white dark:bg-card w-full rounded-xl shadow-2xl p-8 sm:p-10 border border-border/50">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-[#3f444b] dark:text-foreground font-sans tracking-tight">Schedule a Free Consultation</h3>
                <p className="text-muted-foreground text-sm mt-1">Fill out the form below and our specialists will reach out shortly.</p>
              </div>

              <FormProvider formKey="consultation_portal_form_home" formId="consultation_portal_form_home" sectionName="professional-consultation" defaultValues={{
              name: "",
              email: "",
              phone: "",
              organization: "",
              professional_role: "",
              message: "",
              source: "home_page",
              nonce: typeof window !== 'undefined' ? (window as any).wpApiSettings?.nonce || "" : ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              organization: {
                required: true
              }
            }} submitText="Schedule Consultation" successMessage="Thank you! Your consultation request has been received. A GovERP specialist will contact you shortly.">
                <ConsultationForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ConsultationForm() {
  const {
    control,
    resetForm
  } = useWvcForm();
  const {
    handleSubmit: rhfHandleSubmit
  } = useFormContext();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const onFormSubmit = async (data: any) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await wvcClient.formSubmission({
        formId: "consultation_portal_form_home",
        sectionName: "professional-consultation",
        formData: {
          ...data,
          recipient_email: "info@kanha.biz"
        },
        timestamp: Date.now()
      });
      await new Promise(resolve => setTimeout(resolve, 1000));
      setIsSubmitted(true);
    } catch (error) {
      setSubmitError("We encountered an error sending your inquiry to info@kanha.biz. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleReset = () => {
    resetForm();
    setIsSubmitted(false);
    setSubmitError(null);
  };
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center py-12 space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="bg-green-100 dark:bg-green-900/30 p-4 rounded-full">
          <CircleCheck className="size-12 text-green-600 dark:text-green-400" />
        </div>
        <h4 className="text-xl font-bold text-foreground">Request Received</h4>
        <p className="text-muted-foreground">Thank you! We have received your inquiry and a systems specialist will contact you shortly to schedule your consultation.</p>
        <Button onClick={handleReset} variant="outline" className="mt-4 border-[#095fa3] text-[#095fa3] rounded-[4px]">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={rhfHandleSubmit(onFormSubmit)} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Name</FormLabel>
                            <FormControl>
                                <Input placeholder="Jane Doe" className="bg-background border-border focus-visible:ring-primary focus-visible:border-primary h-12 rounded-[4px] transition-all duration-300" {...field} />
                            </FormControl>
                            
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Email Address</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="jane@example.com" className="bg-background border-border focus-visible:ring-primary focus-visible:border-primary h-12 rounded-[4px] transition-all duration-300" {...field} />
                            </FormControl>
							
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Phone Number</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="+1 (555) 123-14567" className="bg-background border-border focus-visible:ring-primary focus-visible:border-primary h-12 rounded-[4px] transition-all duration-300" {...field} />
                            </FormControl>
                            
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="organization" rules={{
        required: "Organization / Agency is required"
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Organization / Agency</FormLabel>
                            <FormControl>
                                <Input placeholder="Department of Finance" className="bg-background border-border focus-visible:ring-primary focus-visible:border-primary h-12 rounded-[4px] transition-all duration-300" {...field} />
                            </FormControl>
							
                            <FormMessage />
                        </FormItem>} />
				
				<FormField control={control} name="professional_role" render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
              <FormLabel className="text-[#3f444b] dark:text-foreground font-medium">Professional Role</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full border-[#e0e0e0] focus:ring-[#095fa3] rounded-[4px]">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Ministry Executive">Ministry Executive</SelectItem>
                  <SelectItem value="CEO or Director">CEO or Director</SelectItem>
                  <SelectItem value="IT Administrator">IT Administrator</SelectItem>
                  <SelectItem value="Other Stakeholder">Other Stakeholder</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
            </div>

            <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Brief Description</FormLabel>
                        <FormControl>
                            <Textarea placeholder="Tell us about your ERP needs or specific questions..." className="bg-background border-border focus-visible:ring-primary focus-visible:border-primary min-h-[120px] rounded-[4px] resize-none transition-all duration-300" {...field} />
                        </FormControl>
                       
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="source" render={({
      field
    }) => <input type="hidden" {...field} />} />

            <FormField control={control} name="nonce" render={({
      field
    }) => <input type="hidden" {...field} />} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-[4px] text-destructive text-sm flex items-start gap-3">
                    <p>{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-bold rounded-[4px] bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md transition-all duration-300 group">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-5" />
                        Processing...
                    </> : <>
                        Schedule Consultation
                        <ArrowRight className="ml-2 size-5 group-hover:translate-x-1 transition-transform" />
                    </>}
            </Button>
        </Form>;
}